/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as NavigationActionCreators from '@app/actions/NavigationActionCreators';
import {GroupDMAvatar} from '@app/components/common/GroupDMAvatar';
import styles from '@app/components/modals/MutualItemsSheet.module.css';
import {GuildIcon} from '@app/components/popouts/GuildIcon';
import {BottomSheet} from '@app/components/uikit/bottom_sheet/BottomSheet';
import {Scroller} from '@app/components/uikit/Scroller';
import {StatusAwareAvatar} from '@app/components/uikit/StatusAwareAvatar';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {GuildRecord} from '@app/records/GuildRecord';
import type {ProfileMutualGuild, ProfileRecord} from '@app/records/ProfileRecord';
import {UserRecord} from '@app/records/UserRecord';
import GuildStore from '@app/stores/GuildStore';
import SelectedChannelStore from '@app/stores/SelectedChannelStore';
import UserProfileMobileStore from '@app/stores/UserProfileMobileStore';
import * as ChannelUtils from '@app/utils/ChannelUtils';
import * as NicknameUtils from '@app/utils/NicknameUtils';
import {ME} from '@fluxer/constants/src/AppConstants';
import type {UserPartial} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import {useLingui} from '@lingui/react/macro';
import {ArrowLeftIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

type MutualItemsView = 'friends' | 'guilds' | 'groups';

interface MutualItemsSheetProps {
	isOpen: boolean;
	onClose: () => void;
	view: MutualItemsView;
	profile: ProfileRecord;
	mutualGroups: ReadonlyArray<ChannelRecord>;
}

export const MutualItemsSheet: React.FC<MutualItemsSheetProps> = observer(
	({isOpen, onClose, view, profile, mutualGroups}) => {
		const {t} = useLingui();

		const title = (() => {
			switch (view) {
				case 'friends':
					return t`Mutual Friends`;
				case 'guilds':
					return t`Mutual Communities`;
				case 'groups':
					return t`Mutual Groups`;
			}
		})();

		return (
			<BottomSheet
				isOpen={isOpen}
				onClose={onClose}
				snapPoints={[0, 1]}
				initialSnap={1}
				disablePadding={true}
				surface="primary"
				showCloseButton={false}
				leadingAction={
					<button type="button" onClick={onClose} className={styles.backButton}>
						<ArrowLeftIcon className={styles.backIcon} weight="bold" />
					</button>
				}
				title={title}
			>
				<Scroller className={styles.container}>
					<div className={styles.list}>
						{view === 'friends' && <MutualFriendsList profile={profile} onClose={onClose} />}
						{view === 'guilds' && <MutualGuildsList profile={profile} onClose={onClose} />}
						{view === 'groups' && <MutualGroupsList groups={mutualGroups} onClose={onClose} />}
					</div>
				</Scroller>
			</BottomSheet>
		);
	},
);

const MutualFriendsList: React.FC<{profile: ProfileRecord; onClose: () => void}> = observer(({profile, onClose}) => {
	const friends = profile.mutualFriends ?? [];

	return (
		<>
			{friends.map((friend: UserPartial) => {
				const friendRecord = new UserRecord(friend);
				return (
					<button
						key={friendRecord.id}
						type="button"
						className={styles.item}
						onClick={() => {
							onClose();
							UserProfileMobileStore.open(friendRecord.id, profile.guildId ?? undefined);
						}}
					>
						<StatusAwareAvatar size={40} user={friendRecord} />
						<div className={styles.itemInfo}>
							<span className={styles.itemName}>
								{NicknameUtils.getNickname(friendRecord, profile.guildId ?? undefined)}
							</span>
							<span className={styles.itemDetail}>{friendRecord.tag}</span>
						</div>
					</button>
				);
			})}
		</>
	);
});

const MutualGuildsList: React.FC<{profile: ProfileRecord; onClose: () => void}> = observer(({profile, onClose}) => {
	const mutualGuilds = profile.mutualGuilds ?? [];

	type MutualGuildDisplay = {guild: GuildRecord; nick: string | null};
	const items = useMemo(
		() =>
			mutualGuilds
				.map((mg: ProfileMutualGuild) => {
					const guild = GuildStore.getGuild(mg.id);
					if (!guild) return null;
					return {guild, nick: mg.nick};
				})
				.filter((item): item is MutualGuildDisplay => item !== null),
		[mutualGuilds],
	);

	return (
		<>
			{items.map(({guild, nick}) => (
				<button
					key={guild.id}
					type="button"
					className={styles.item}
					onClick={() => {
						onClose();
						UserProfileMobileStore.close();
						const selectedChannel = SelectedChannelStore.selectedChannelIds.get(guild.id);
						NavigationActionCreators.selectGuild(guild.id, selectedChannel);
					}}
				>
					<GuildIcon id={guild.id} name={guild.name} icon={guild.icon} className={styles.guildIcon} sizePx={40} />
					<div className={styles.itemInfo}>
						<span className={styles.itemName}>{guild.name}</span>
						{nick && <span className={styles.itemDetail}>{nick}</span>}
					</div>
				</button>
			))}
		</>
	);
});

const MutualGroupsList: React.FC<{groups: ReadonlyArray<ChannelRecord>; onClose: () => void}> = observer(
	({groups, onClose}) => {
		const {t} = useLingui();

		return (
			<>
				{groups.map((group) => {
					const memberCount = group.recipientIds.length + 1;
					const memberLabel = memberCount === 1 ? t`${memberCount} Member` : t`${memberCount} Members`;

					return (
						<button
							key={group.id}
							type="button"
							className={styles.item}
							onClick={() => {
								onClose();
								UserProfileMobileStore.close();
								NavigationActionCreators.selectChannel(ME, group.id);
							}}
						>
							<GroupDMAvatar channel={group} size={40} />
							<div className={styles.itemInfo}>
								<span className={styles.itemName}>{ChannelUtils.getDMDisplayName(group)}</span>
								<span className={styles.itemDetail}>{memberLabel}</span>
							</div>
						</button>
					);
				})}
			</>
		);
	},
);
